// ─── Simpan produk ❤ — "Produk Disimpan" pembeli (mig 6270) ─────────────────
//
// Pembeli yang login menyimpan ke AKUN (tabel `saved_products`, hanya lewat
// tiga RPC di `src/lib/supabase/savedProducts.ts`); tamu menyimpan di
// PERANGKAT (localStorage per toko) dan simpanan itu digabung ke akunnya
// begitu ia masuk (`save_products_bulk`, lalu simpanan perangkat dikosongkan)
// — kecuali di browser yang memegang sesi admin toko (impersonasi pelanggan
// dari panel admin: simpanan perangkat itu milik si admin).
//
// Modul ini murni supaya bentuk simpanan tamu, batas, urutan gabung, ketuk
// dua kali, dan teks umpan balik diuji di Vitest. Pembungkus localStorage di
// bawah best-effort, tak pernah throw (idiom `recentlyViewed.ts`).

import { ADMIN_AUTH_STORAGE_KEY } from "@/lib/supabase/scope";

/**
 * Cermin batas per akun di RPC — tamu dibatasi sama. Sejak mig 6290 yang
 * dihitung hanya simpanan produk AKTIF (yang memang tampil di daftar).
 */
export const SAVED_PRODUCTS_LIMIT = 500;

/**
 * Cermin batas `save_products_bulk` per panggilan (mig 6290) — SAMA dengan
 * batas simpanan tamu, jadi seluruh simpanan perangkat masuk dalam SATU
 * panggilan: satu transaksi, urutan perangkat terjaga. (Dulu 100 per panggilan:
 * batch yang lebih lama mendarat dengan `now()` lebih baru → urutan terbalik.)
 */
export const SAVED_MERGE_MAX = 500;

/** Prefiks kunci localStorage simpanan tamu; akhiran `:<storeId>`. */
export const GUEST_SAVED_KEY = "galea-saved-products";

/** Jarak maksimum dua ketukan untuk dihitung "ketuk dua kali". */
export const DOUBLE_TAP_MS = 320;

/** Dua ketukan harus di titik yang kira-kira sama (bukan dua geseran pendek). */
export const DOUBLE_TAP_SLOP_PX = 32;

/** Satu simpanan tamu: id produk + kapan disimpan (epoch ms). */
export type GuestSavedEntry = { id: string; at: number };

/** Satu baris daftar akun (`list_my_saved_products`). */
export type SavedProductRow = { productId: string; savedAt: string };

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Jalur tempat fitur ini hidup. Panel admin/platform punya sesi lain, dan
 * katalog reseller `/toko` white-label — pembeli akhirnya bukan akun toko ini,
 * jadi tak ada ❤, tak ada gabung simpanan, tak ada query di sana.
 */
export function savedProductsEnabledOnPath(pathname: string | null | undefined): boolean {
  if (!pathname) return false;
  return !["/admin", "/platform", "/toko"].some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

export function guestSavedKey(storeId: string): string {
  return `${GUEST_SAVED_KEY}:${storeId}`;
}

function isGuestEntry(item: unknown): item is GuestSavedEntry {
  if (typeof item !== "object" || item === null) return false;
  const { id, at } = item as Partial<GuestSavedEntry>;
  return typeof id === "string" && UUID_RE.test(id) && typeof at === "number" && Number.isFinite(at);
}

/**
 * Daftar tamu yang sah: terbaru dulu, tanpa ganda (yang terbaru menang), id
 * berbentuk uuid, maksimal `SAVED_PRODUCTS_LIMIT`.
 */
export function normalizeGuestSaved(items: readonly unknown[]): GuestSavedEntry[] {
  const seen = new Set<string>();
  return items
    .filter(isGuestEntry)
    .map(({ id, at }) => ({ id, at }))
    .sort((a, b) => b.at - a.at)
    .filter((item) => (seen.has(item.id) ? false : (seen.add(item.id), true)))
    .slice(0, SAVED_PRODUCTS_LIMIT);
}

/**
 * Isi localStorage → daftar tamu. JSON korup / bentuk asing → kosong
 * (simpanan opsional tak boleh menjatuhkan halaman).
 */
export function parseGuestSaved(raw: string | null | undefined): GuestSavedEntry[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? normalizeGuestSaved(parsed) : [];
  } catch {
    return [];
  }
}

/**
 * Simpan / lepas satu produk di daftar tamu. Penuh → tidak berubah dan
 * `full: true` (cermin penolakan RPC — yang lama tak dibuang diam-diam).
 */
export function toggleGuestSaved(
  list: readonly GuestSavedEntry[],
  id: string,
  saved: boolean,
  now: number
): { list: GuestSavedEntry[]; changed: boolean; full: boolean } {
  const has = list.some((item) => item.id === id);
  if (saved === has) return { list: [...list], changed: false, full: false };
  if (!saved) return { list: list.filter((item) => item.id !== id), changed: true, full: false };
  if (list.length >= SAVED_PRODUCTS_LIMIT) return { list: [...list], changed: false, full: true };
  return { list: [{ id, at: now }, ...list], changed: true, full: false };
}

/**
 * Id tamu untuk digabung ke akun dalam SATU panggilan, terbaru dulu (urutan
 * larik = urutan di daftar akun; kuota yang tersisa diberikan ke yang terbaru).
 */
export function guestMergeIds(list: readonly GuestSavedEntry[]): string[] {
  return normalizeGuestSaved(list)
    .map((item) => item.id)
    .slice(0, SAVED_MERGE_MAX);
}

/**
 * Umpan balik sesudah gabung. `accountIds` = daftar akun SESUDAH gabung
 * (`null` = tak terbaca). Simpanan perangkat yang tak ada di daftar akun
 * padahal daftar itu sudah penuh = tak muat → dikabarkan, bukan dibuang
 * diam-diam. Yang tak ikut karena sudah tersimpan / produknya nonaktif bukan
 * kabar buruk dan tak disebut.
 */
export function guestMergeFeedback(
  pendingIds: readonly string[],
  added: number,
  accountIds: readonly string[] | null
): { kind: "merged" | "merged-partial"; count: number } | null {
  if (accountIds && accountIds.length >= SAVED_PRODUCTS_LIMIT) {
    const inAccount = new Set(accountIds);
    if (pendingIds.some((id) => !inAccount.has(id))) return { kind: "merged-partial", count: added };
  }
  return added > 0 ? { kind: "merged", count: added } : null;
}

/**
 * Browser ini memegang sesi admin toko (cookie `ADMIN_AUTH_STORAGE_KEY`, bisa
 * ber-chunk `.0`/`.1` bila sesinya besar)? Pelanggan sungguhan tak pernah
 * punya cookie itu; admin yang "Login sebagai pelanggan" membuka akun
 * pelanggan DI BROWSER-NYA SENDIRI — simpanan ❤ perangkatnya milik si admin
 * dan tak boleh ikut pindah ke akun pelanggan.
 */
export function hasAdminSessionCookie(cookie: string | null | undefined): boolean {
  if (!cookie) return false;
  return cookie.split(";").some((part) => {
    const eq = part.indexOf("=");
    if (eq < 0) return false;
    const name = part.slice(0, eq).trim();
    const isAdminCookie =
      name === ADMIN_AUTH_STORAGE_KEY ||
      (name.startsWith(`${ADMIN_AUTH_STORAGE_KEY}.`) &&
        /^\d+$/.test(name.slice(ADMIN_AUTH_STORAGE_KEY.length + 1)));
    return isAdminCookie && part.slice(eq + 1).trim().length > 0;
  });
}

/**
 * Pembaruan optimistik daftar akun: simpan = naik ke paling atas, lepas =
 * keluar. Tak mengubah apa pun bila keadaannya sudah sama.
 */
export function applySavedToggle(
  rows: readonly SavedProductRow[],
  productId: string,
  saved: boolean,
  savedAt: string
): SavedProductRow[] {
  const has = rows.some((row) => row.productId === productId);
  if (saved === has) return [...rows];
  if (!saved) return rows.filter((row) => row.productId !== productId);
  return [{ productId, savedAt }, ...rows];
}

export type TapPoint = { at: number; x: number; y: number };

/** Ketukan kedua cukup dekat (waktu & jarak) dengan yang pertama. */
export function isDoubleTap(
  previous: TapPoint | null,
  next: TapPoint,
  ms: number = DOUBLE_TAP_MS,
  slop: number = DOUBLE_TAP_SLOP_PX
): boolean {
  if (!previous) return false;
  const gap = next.at - previous.at;
  if (gap <= 0 || gap > ms) return false;
  return Math.hypot(next.x - previous.x, next.y - previous.y) <= slop;
}

export type SavedFailure = "limit" | "unavailable" | "auth" | "network";

/** SQLSTATE dari RPC mig 6270 → jenis kegagalan untuk teks umpan balik. */
export function classifySavedFailure(code: string | null | undefined): SavedFailure {
  switch (code) {
    case "54000":
      return "limit";
    case "P0002":
      return "unavailable";
    case "42501":
    case "PGRST301":
    case "PGRST303":
      return "auth";
    default:
      return "network";
  }
}

export type SavedFeedback =
  | "saved"
  | "removed"
  | "guest-saved"
  | "merged"
  | "merged-partial"
  | SavedFailure;

/** Teks toast. `count` hanya untuk "merged*" (jumlah yang pindah ke akun). */
export function savedFeedbackText(kind: SavedFeedback, count = 0): string {
  switch (kind) {
    case "saved":
      return "❤ Disimpan — buka di Akun › Produk Disimpan";
    case "removed":
      return "Dihapus dari Produk Disimpan";
    case "guest-saved":
      return "❤ Disimpan di HP ini — masuk agar tersimpan di akunmu";
    case "merged":
      return `${count} produk simpanan di HP ini kini tersimpan di akunmu`;
    case "merged-partial":
      return count > 0
        ? `${count} produk simpanan di HP ini pindah ke akunmu — sisanya tak muat, Produk Disimpan sudah ${SAVED_PRODUCTS_LIMIT}`
        : `Simpanan di HP ini tak muat — Produk Disimpan di akunmu sudah ${SAVED_PRODUCTS_LIMIT}`;
    case "limit":
      return `Produk Disimpan sudah ${SAVED_PRODUCTS_LIMIT} — hapus beberapa dulu ya`;
    case "unavailable":
      return "Produk ini sudah tidak tersedia";
    case "auth":
      return "Sesi masuk habis — masuk lagi untuk menyimpan";
    case "network":
      return "Gagal menyimpan. Periksa internet lalu coba lagi.";
  }
}

// ─── Pembungkus localStorage (best-effort, tak pernah throw) ────────────────

export function readGuestSaved(storeId: string | null): GuestSavedEntry[] {
  if (!storeId || typeof window === "undefined") return [];
  try {
    return parseGuestSaved(window.localStorage.getItem(guestSavedKey(storeId)));
  } catch {
    return [];
  }
}

export function writeGuestSaved(storeId: string | null, list: readonly GuestSavedEntry[]): void {
  if (!storeId || typeof window === "undefined") return;
  try {
    if (list.length) {
      window.localStorage.setItem(guestSavedKey(storeId), JSON.stringify(list));
    } else {
      window.localStorage.removeItem(guestSavedKey(storeId));
    }
  } catch {
    // abaikan — penyimpanan penuh / mode privat
  }
}
